import { useEffect, type DependencyList, type RefObject } from 'react';

/** How close to the end (px) still counts as "at the latest message". */
export const PIN_SLACK = 80;
export const nearEnd = (el: Pick<HTMLElement, 'scrollHeight' | 'scrollTop' | 'clientHeight'>) => el.scrollHeight - el.scrollTop - el.clientHeight < PIN_SLACK;

/**
 * t236u: on the phone, keep the latest message just above the composer. Tapping into the composer jumps to the latest
 * (even after scrolling up to read) and pins it; while pinned, the list stays at the end as the keyboard opens and the
 * visual viewport resizes (visualViewport resize/scroll, no timers), as the composer grows with more lines, and as
 * new messages arrive. Scrolling up unpins until the next tap into the composer. `onPin` lets a page sync its own
 * "at bottom" state (ConversationPage's scroll restoration and older-page loading keep working as before).
 */
export function usePinToLatest(list: RefObject<HTMLElement | null>, composer: RefObject<HTMLElement | null>, o: { enabled: boolean; onPin?: () => void }, deps: DependencyList = []) {
  const { enabled, onPin } = o;
  useEffect(() => {
    const el = list.current;
    const box = composer.current;
    if (!enabled || !el || !box) return;
    let pinned = nearEnd(el);
    let raf = 0;
    const toEnd = () => { el.scrollTop = el.scrollHeight; };
    const keep = () => { if (pinned) toEnd(); };
    const onScroll = () => { pinned = nearEnd(el); };
    const onFocus = () => {
      pinned = true; onPin?.(); toEnd();
      cancelAnimationFrame(raf); raf = requestAnimationFrame(toEnd); // after the composer's own layout change
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    box.addEventListener('focusin', onFocus);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', keep);
    vv?.addEventListener('scroll', keep);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(keep);
    ro?.observe(el); ro?.observe(box);
    const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(keep);
    mo?.observe(el, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', onScroll);
      box.removeEventListener('focusin', onFocus);
      vv?.removeEventListener('resize', keep);
      vv?.removeEventListener('scroll', keep);
      ro?.disconnect(); mo?.disconnect();
    };
  }, [enabled, onPin, list, composer, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps -- deps re-attach when the list or composer mounts
}
